import {
  useRevertTagWriteMutation,
  type InFlightAgentActionProposal,
  type InFlightTagWriteProposal
} from '@axilon-apps/domains-ontology-ui-state'
import { registerAgentActionView, type AgentActionViewCapability } from './viewRegistry'

export interface TagWriteDisplayModel {
  targetLabel: string
  currentValue: string
  proposedValue: string
  policy: string
  expiry: string
  trend: string[]
  priorValue?: string
  reversibleEventId?: string
}

function primitiveText(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (value === null) return 'null'
  if (value === undefined) return 'unavailable'
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function targetLabel(proposal: InFlightTagWriteProposal): string {
  const target = proposal.target
  if (target) {
    for (const key of ['name', 'display_name', 'tag_path', 'path', 'id']) {
      const value = target[key]
      if (typeof value === 'string' && value.trim()) return value
    }
  }
  return proposal.tagId
}

function trendPointText(point: unknown): string {
  if (!point || typeof point !== 'object' || Array.isArray(point)) return primitiveText(point)
  const row = point as Record<string, unknown>
  const value = row.value ?? row.avg ?? row.last ?? row.y
  const timestamp = row.timestamp ?? row.time ?? row.ts ?? row.x
  if (timestamp === undefined) return primitiveText(value ?? point)
  return `${primitiveText(timestamp)}: ${primitiveText(value)}`
}

/** Convert loose proxy preview/result payloads into stable card copy. */
export function buildTagWriteDisplayModel(
  proposal: InFlightTagWriteProposal
): TagWriteDisplayModel {
  return {
    targetLabel: targetLabel(proposal),
    currentValue: primitiveText(proposal.currentValue),
    proposedValue: primitiveText(proposal.proposedValue),
    policy: primitiveText(proposal.policySnapshot),
    expiry: proposal.expiresAt
      ? new Date(proposal.expiresAt).toLocaleString()
      : 'No expiry supplied',
    trend: (proposal.trend ?? []).slice(-6).map(trendPointText),
    ...(proposal.priorValue !== undefined
      ? { priorValue: primitiveText(proposal.priorValue) }
      : {}),
    ...(proposal.reversibleEventId ? { reversibleEventId: proposal.reversibleEventId } : {})
  }
}

function isTagWrite(proposal: InFlightAgentActionProposal): proposal is InFlightTagWriteProposal {
  return proposal.kind === 'tag_write'
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="break-words">
      <span className="text-muted-foreground">{label}:</span> {value}
    </div>
  )
}

function TagWriteProposalDetail({ proposal }: { proposal: InFlightTagWriteProposal }) {
  const model = buildTagWriteDisplayModel(proposal)
  return (
    <div className="rounded-xl border border-line/70 bg-muted/30 p-2 text-11 text-foreground">
      <div className="grid gap-1.5">
        <DetailRow label="Target" value={model.targetLabel} />
        <DetailRow label="Tag id" value={proposal.tagId} />
        <DetailRow label="Current value" value={model.currentValue} />
        <DetailRow label="Proposed value" value={model.proposedValue} />
        <DetailRow label="Policy snapshot" value={model.policy} />
        <DetailRow label="Reason" value={proposal.reason} />
        <DetailRow label="Expires" value={model.expiry} />
        {proposal.revertCondition ? (
          <DetailRow label="Revert condition" value={primitiveText(proposal.revertCondition)} />
        ) : null}
        {model.trend.length > 0 ? (
          <div className="break-words">
            <span className="text-muted-foreground">Recent trend:</span>
            <ol className="mt-0.5 list-inside list-decimal font-mono text-10">
              {model.trend.map((point, index) => (
                <li key={`${index}-${point}`}>{point}</li>
              ))}
            </ol>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function TagWriteSettledSummary({ proposal }: { proposal: InFlightTagWriteProposal }) {
  const [revert, revertState] = useRevertTagWriteMutation()
  const terminal = proposal.terminalState
  if (!terminal) return null
  if (terminal.kind === 'apply_failed') {
    return <p className="px-1 text-10 text-destructive">Write failed — {terminal.error}</p>
  }
  if (terminal.kind === 'rejected') {
    return (
      <p className="px-1 text-10 text-muted-foreground">
        Rejected{proposal.rejectionReason ? ` — ${proposal.rejectionReason}` : ''}.
      </p>
    )
  }
  const model = buildTagWriteDisplayModel(proposal)
  const revertError =
    revertState.error && typeof revertState.error === 'object' && 'data' in revertState.error
      ? String(
          (revertState.error as { data?: { error?: unknown } }).data?.error ?? 'Revert failed.'
        )
      : revertState.error
        ? 'Revert failed.'
        : null
  return (
    <div className="flex items-center justify-between gap-2 px-1 text-10">
      <p className="text-emerald-600 dark:text-emerald-400">
        {revertState.isSuccess
          ? 'Reverted through the audited writeback rail.'
          : `Applied${model.priorValue !== undefined ? ` — prior value ${model.priorValue}` : ''}.`}
        {revertError ? <span className="ml-1 text-destructive">{revertError}</span> : null}
      </p>
      {model.reversibleEventId && !revertState.isSuccess ? (
        <button
          type="button"
          disabled={revertState.isLoading}
          onClick={() => {
            void revert({ eventId: model.reversibleEventId! })
          }}
          className="shrink-0 cursor-pointer rounded-full border border-line bg-background px-2 py-1 font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        >
          {revertState.isLoading ? 'Reverting…' : 'Revert'}
        </button>
      ) : null}
    </div>
  )
}

export const tagWriteViewCapability: AgentActionViewCapability = {
  kind: 'tag_write',
  badgeLabel() {
    return 'tag write'
  },
  renderDetail(proposal) {
    if (!isTagWrite(proposal)) return null
    return <TagWriteProposalDetail proposal={proposal} />
  },
  renderSettledSummary(proposal) {
    if (!isTagWrite(proposal)) return null
    return <TagWriteSettledSummary proposal={proposal} />
  }
}

registerAgentActionView(tagWriteViewCapability)
